import { FoodsPagination } from "@foodpilot/foods";
import { Box, PaginationItem, useTheme } from "@mui/material";
import type { PaginationRenderItemParams } from "@mui/material";

type ReferencePaginationProps = {
    disabled?: boolean;
    onPageChange: (page: number) => void;
    page: number;
    pageCount: number;
};

/**
 * Figma reference-search pagination (12803:67915 / 12803:67987 / 13627:68371).
 * FoodsPagination owns pagination behavior; this composition removes its questionnaire
 * progress rail and maps the selected state to the active application theme.
 */
export const ReferencePagination = ({ disabled = false, onPageChange, page, pageCount }: ReferencePaginationProps) => {
    const theme = useTheme();

    const renderItem = (params: PaginationRenderItemParams) => (
        <PaginationItem
            {...params}
            data-testid={params.selected ? "reference-pagination-current-page" : undefined}
            sx={{
                backgroundColor: theme.custom.grey[100],
                border: 0,
                borderRadius: "50%",
                color: theme.custom.grey[2800],
                fontWeight: theme.typography.fontWeightBold,
                height: theme.spacing(4),
                margin: 0,
                minWidth: theme.spacing(4),
                padding: 0,
                width: theme.spacing(4),
                "&.Mui-selected": {
                    backgroundColor: theme.palette.primary[600],
                    color: theme.custom.grey[100],
                },
                "&.Mui-selected:hover": {
                    backgroundColor: theme.palette.primary[700],
                },
                "&:hover": {
                    backgroundColor: theme.custom.grey[300],
                },
                "& .MuiSvgIcon-root": {
                    height: theme.spacing(2.5),
                    width: theme.spacing(2.5),
                },
            }}
        />
    );

    return (
        <Box
            data-testid="reference-pagination"
            sx={{
                maxWidth: "100%",
                width: theme.spacing(40),
                "& > .MuiBox-root": {
                    width: "100%",
                },
                "& > .MuiBox-root > .MuiBox-root:first-of-type": {
                    display: "none",
                },
                "& .MuiPagination-root": {
                    width: "100%",
                },
                "& .MuiPagination-ul": {
                    flexWrap: "nowrap",
                    gap: theme.spacing(1),
                    justifyContent: "flex-end",
                },
                "& .MuiPagination-ul > li:first-of-type": {
                    marginRight: theme.spacing(0.5),
                },
                "& .MuiPagination-ul > li:last-of-type": {
                    marginLeft: theme.spacing(0.5),
                },
            }}
        >
            <FoodsPagination
                page={page}
                pages={Array.from({ length: pageCount }, (_value, index) => ({ label: String(index + 1) }))}
                PaginationProps={{ disabled, variant: "text" }}
                renderItem={renderItem}
                setPage={onPageChange}
            />
        </Box>
    );
};
